import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { BUSINESS, SERVICES } from "../data/site";

const bySlugs = (slugs) => SERVICES.filter((s) => slugs.includes(s.slug));

const DROPDOWNS = [
  {
    label: "Services",
    viewAll: "/services",
    items: bySlugs([
      "smog-check-hayward",
      "star-smog-check-hayward",
      "diesel-smog-check-hayward",
      "clean-truck-check-hayward",
      "vin-verification-hayward",
      "smog-repair-hayward",
    ]),
  },
  {
    label: "Smog Check",
    items: bySlugs([
      "smog-check-hayward",
      "star-smog-check-hayward",
      "diesel-smog-check-hayward",
      "clean-truck-check-hayward",
    ]),
  },
];

const FLAT_LINKS = [
  { label: "Home", href: "/" },
  { label: "Pricing", href: "/pricing" },
  { label: "Coupons", href: "/coupons" },
  { label: "Blog", href: "/blog" },
  { label: "Locations", href: "/locations/hayward" },
  { label: "Contact", href: "/contact" },
];

const linkCls =
  "relative whitespace-nowrap font-display text-[13px] xl:text-sm font-semibold uppercase tracking-[0.12em] text-slate-700 px-3 py-2 rounded-full hover:text-volt hover:bg-volt-dim transition-[color,background-color] duration-300 after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:h-[2px] after:w-0 after:bg-volt after:transition-[width] after:duration-300 hover:after:w-2/3";

const Dropdown = ({ group }) => (
  <div className="relative group">
    <button
      data-testid={`nav-dropdown-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex items-center gap-1.5 py-2 ${linkCls}`}
      aria-haspopup="true"
    >
      {group.label}
      <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-300" />
    </button>
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-[opacity,transform,visibility] duration-300 z-50">
      <div className="w-64 bg-white border border-line rounded-xl shadow-xl py-2 overflow-hidden">
        {group.items.map((s) => (
          <Link
            key={s.slug}
            data-testid={`nav-item-${group.label.toLowerCase().replace(/\s+/g, "-")}-${s.slug}`}
            to={`/services/${s.slug}`}
            className="block px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-volt hover:bg-volt-dim transition-colors duration-200"
          >
            {s.title}
          </Link>
        ))}
        {group.viewAll && (
          <Link
            data-testid="nav-item-all-services"
            to={group.viewAll}
            className="flex items-center justify-between px-5 py-2.5 mt-1 border-t border-line text-sm font-bold text-volt hover:bg-volt-dim transition-colors duration-200"
          >
            View All Services
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const navigate = useNavigate();

  const go = (href) => {
    setOpen(false);
    setOpenGroup(null);
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      navigate(path || "/");
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      navigate(href);
    }
  };

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src="/logo.webp"
            alt="BaySmog — STAR Smog Check Hayward, CA"
            width="320"
            height="216"
            className="h-12 sm:h-14 w-auto group-hover:scale-[1.04] transition-transform duration-300"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 xl:gap-2 flex-nowrap" aria-label="Primary">
          <button data-testid="nav-link-home" onClick={() => go("/")} className={linkCls}>
            Home
          </button>
          {DROPDOWNS.map((d) => (
            <Dropdown key={d.label} group={d} />
          ))}
          {FLAT_LINKS.slice(1).map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className={linkCls}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-button"
            href={BUSINESS.phoneHref}
            className="hidden sm:flex items-center gap-2 whitespace-nowrap bg-gradient-to-r from-volt to-[#0B4AC2] text-white rounded-full font-display font-bold uppercase tracking-wide text-sm px-6 py-3 shadow-md shadow-volt/30 hover:bg-volt-hover hover:-translate-y-0.5 transition-[background-color,transform] duration-300 animate-pulse-volt"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phone}
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="lg:hidden w-11 h-11 flex items-center justify-center border border-line text-ink hover:border-volt hover:text-volt transition-colors duration-300"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-line bg-white/95 backdrop-blur-xl"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {FLAT_LINKS.slice(0, 1).map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="flex items-center justify-between py-4 border-b border-line font-display font-bold uppercase tracking-wide text-lg text-left text-ink hover:text-volt transition-colors duration-300"
                >
                  {l.label}
                  <ArrowUpRight className="w-5 h-5 text-volt" />
                </button>
              ))}

              {DROPDOWNS.map((d) => (
                <div key={d.label} className="border-b border-line">
                  <button
                    data-testid={`mobile-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setOpenGroup(openGroup === d.label ? null : d.label)}
                    aria-expanded={openGroup === d.label}
                    className="w-full flex items-center justify-between py-4 font-display font-bold uppercase tracking-wide text-lg text-left text-ink hover:text-volt transition-colors duration-300"
                  >
                    {d.label}
                    <ChevronDown
                      className={`w-5 h-5 text-volt transition-transform duration-300 ${openGroup === d.label ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openGroup === d.label && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-4 pl-4 flex flex-col">
                          {d.items.map((s) => (
                            <button
                              key={s.slug}
                              data-testid={`mobile-nav-item-${s.slug}`}
                              onClick={() => go(`/services/${s.slug}`)}
                              className="py-2.5 text-left text-sm font-semibold text-slate-500 hover:text-volt transition-colors duration-200"
                            >
                              {s.title}
                            </button>
                          ))}
                          {d.viewAll && (
                            <button
                              data-testid="mobile-nav-item-all-services"
                              onClick={() => go(d.viewAll)}
                              className="py-2.5 text-left text-sm font-bold text-volt"
                            >
                              View All Services
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {FLAT_LINKS.slice(1).map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="flex items-center justify-between py-4 border-b border-line font-display font-bold uppercase tracking-wide text-lg text-left text-ink hover:text-volt transition-colors duration-300"
                >
                  {l.label}
                  <ArrowUpRight className="w-5 h-5 text-volt" />
                </button>
              ))}

              <a
                data-testid="mobile-nav-call-button"
                href={BUSINESS.phoneHref}
                className="mt-5 flex items-center justify-center gap-2 bg-volt text-white rounded-full font-display font-bold uppercase px-6 py-4"
              >
                <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
